import { useState, useEffect } from 'react';
import { X, FileText, Calendar, User, CheckCircle, Check, Trash2 } from 'lucide-react';
import { getJournalEntry } from '../api/gl';
import { useAuth } from '../contexts/AuthContext';

const JournalEntryViewModal = ({ isOpen, onClose, entryId, onApprove, onDelete }) => {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && entryId) {
      fetchEntry();
    }
  }, [isOpen, entryId]);

  const fetchEntry = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getJournalEntry(entryId);
      setEntry(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load journal entry');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      posted: 'bg-green-100 text-green-800',
      reversed: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Journal Entry Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          ) : entry ? (
            <div className="space-y-6">
              {/* Entry Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Entry Number</label>
                    <p className="text-lg font-semibold text-amber-600">{entry.entry_number}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Entry Date
                    </label>
                    <p className="text-gray-900">{formatDate(entry.entry_date)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Type</label>
                    <p className="text-gray-900 capitalize">{entry.entry_type?.replace('_', ' ')}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Status
                    </label>
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded ${getStatusBadge(entry.status)}`}>
                      {entry.status?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  {entry.reference_number && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Reference Number</label>
                      <p className="text-gray-900">{entry.reference_number}</p>
                    </div>
                  )}
                  {entry.created_by_name && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                        <User className="w-4 h-4" />
                        Created By
                      </label>
                      <p className="text-gray-900">{entry.created_by_name}</p>
                      <p className="text-sm text-gray-500">{formatDate(entry.created_at)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {entry.description && (
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    Description
                  </label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded mt-1">{entry.description}</p>
                </div>
              )}

              {/* Line Items */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Line Items</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {entry.lines?.map((line, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">
                            <div className="font-medium text-gray-900">{line.account_code}</div>
                            <div className="text-gray-500 text-xs">{line.account_name}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {line.description || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium">
                            {line.debit_amount > 0 ? formatCurrency(line.debit_amount) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium">
                            {line.credit_amount > 0 ? formatCurrency(line.credit_amount) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="2" className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                          Total:
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right border-t-2 border-gray-300">
                          {formatCurrency(entry.total_debit)}
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right border-t-2 border-gray-300">
                          {formatCurrency(entry.total_credit)}
                        </td>
                      </tr>
                      {entry.total_debit !== entry.total_credit && (
                        <tr>
                          <td colSpan="4" className="px-4 py-2">
                            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                              Warning: Entry is out of balance! Debits and credits must be equal.
                            </div>
                          </td>
                        </tr>
                      )}
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Notes */}
              {entry.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Notes</label>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded mt-1">{entry.notes}</p>
                </div>
              )}

              {/* Metadata */}
              <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600 space-y-1">
                <p><span className="font-medium">Created:</span> {formatDate(entry.created_at)}</p>
                {entry.updated_at && entry.updated_at !== entry.created_at && (
                  <p><span className="font-medium">Last Updated:</span> {formatDate(entry.updated_at)}</p>
                )}
                {entry.posted_at && (
                  <p><span className="font-medium">Posted:</span> {formatDate(entry.posted_at)}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">No entry data available</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between p-6 border-t border-gray-200">
          <div className="flex gap-3">
            {entry && (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin') && (
              <>
                {(entry.status === 'draft' || entry.status === 'pending_approval') && onApprove && (
                  <button
                    onClick={() => {
                      onApprove(entry.id);
                      onClose();
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    Approve
                  </button>
                )}
                {(entry.status === 'draft' || entry.status === 'pending_approval') && onDelete && (
                  <button
                    onClick={() => {
                      onDelete(entry.id);
                      onClose();
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                )}
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default JournalEntryViewModal;
